import React, { useEffect, useState, useMemo } from "react";
import { OttModerationPanel } from "@/components/ott/OttModerationPanel";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Truck, IndianRupee, Package, Clock, CheckCircle2, Loader2, ExternalLink, Filter, MapPin, Phone, User as UserIcon, MessageSquare, Wallet, AlertCircle, Send, Bug, X as CloseX, Image as ImageIcon, Check, Undo2, Database, Upload, KeyRound, Copy, Trash2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { GoldSettingsPanel } from "@/components/admin/GoldSettingsPanel";
import { useAuth } from "@/context/AuthContext";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import FloatingSlot from "@/components/FloatingSlot";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "ready", label: "Ready" },
  { key: "delivered", label: "Delivered" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  confirmed: "bg-blue-50 text-blue-800 border-blue-200",
  ready: "bg-indigo-50 text-indigo-800 border-indigo-200",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

const OPS_HEAD = {
  redemptions: ["Assisted metal redemptions", "Coordinate couriers, confirm deliveries, and mark dealer settlements."],
  notifications: ["SMS log", "Every outbound notification and its delivery status."],
  payouts: ["Payouts", "Dealer and partner settlements."],
  feedback: ["User feedback", "Bug reports and suggestions from users."],
  sources: ["Data sources", "Health of the external data feeds."],
  ott: ["OTT moderation", "Review new creators' videos, reports and copyright complaints. Every decision is audited."],
  market: ["Market rates", "Gold duty, AIDC and GST assumptions applied to the live gold-api.com spot price."],
};

export default function OpsDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("redemptions");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [feedbackKind, setFeedbackKind] = useState("");
  const [lightbox, setLightbox] = useState(null);          // { url, feedback_id, meta } | null
  const [loadingShot, setLoadingShot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [settleFilter, setSettleFilter] = useState("");
  const [selected, setSelected] = useState(null);           // redemption being assigned
  const [assignForm, setAssignForm] = useState({ courier_partner: "", courier_tracking_id: "", ops_notes: "" });
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [testForm, setTestForm] = useState({ to: "", body: "Find2Near test SMS ✅" });
  const [sendingTest, setSendingTest] = useState(false);

  // Data Sources tab state
  const [sourceStatus, setSourceStatus] = useState(null);
  const [partners, setPartners] = useState([]);
  const [importPreview, setImportPreview] = useState(null);          // { rows, filename }
  const [importSourceLabel, setImportSourceLabel] = useState("partner_import");
  const [importPartnerName, setImportPartnerName] = useState("Ops CSV Import");
  const [importing, setImporting] = useState(false);
  const [partnerForm, setPartnerForm] = useState({ partner_slug: "", partner_name: "", description: "" });
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [newPartnerToken, setNewPartnerToken] = useState(null);      // { partner_slug, token, webhook_url }
  const [pendingList, setPendingList] = useState([]);

  const isAdmin = user?.is_admin;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, r, n, p, f] = await Promise.all([
        api.get("/ops/stats"),
        api.get("/ops/redemptions", {
          params: {
            only_assisted: true,
            status: statusFilter || undefined,
            settlement_status: settleFilter || undefined,
            limit: 200,
          },
        }),
        api.get("/ops/notifications", { params: { limit: 50 } }).catch(() => ({ data: [] })),
        api.get("/ops/payouts", { params: { limit: 50 } }).catch(() => ({ data: [] })),
        api.get("/ops/feedback", { params: { limit: 100, kind: feedbackKind || undefined } }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setRows(r.data);
      setNotifs(n.data);
      setPayouts(p.data);
      setFeedback(f.data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error("Could not load ops data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdmin) loadAll();
  }, [user, isAdmin, statusFilter, settleFilter, feedbackKind]);

  // Auto-refresh feedback every 60s while the ops user is on that tab, so new bug reports
  // land without a manual reload. No poll for other tabs (they're less time-sensitive).
  useEffect(() => {
    if (!(user && isAdmin) || tab !== "feedback") return;
    const t = setInterval(() => {
      api.get("/ops/feedback", { params: { limit: 100, kind: feedbackKind || undefined } })
        .then(r => setFeedback(r.data))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [user, isAdmin, tab, feedbackKind]);

  // Lazy-load Data Sources when tab opens.
  useEffect(() => {
    if (!(user && isAdmin) || tab !== "sources") return;
    Promise.all([
      api.get("/discover/sources").then(r => setSourceStatus(r.data)).catch(() => setSourceStatus(null)),
      api.get("/ops/partners").then(r => setPartners(r.data)).catch(() => setPartners([])),
      api.get("/ops/pending-listings").then(r => setPendingList(r.data)).catch(() => setPendingList([])),
    ]);
  }, [user, isAdmin, tab]);

  const approvePending = async (listing_id) => {
    try {
      await api.patch(`/ops/pending-listings/${listing_id}/approve`);
      setPendingList(list => list.filter(x => x.listing_id !== listing_id));
      toast.success("Approved and live");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectPending = async (listing_id) => {
    const reason = window.prompt("Reason for rejection (optional):", "Spam or unverifiable");
    if (reason === null) return;
    try {
      await api.delete(`/ops/pending-listings/${listing_id}`, { params: { reason } });
      setPendingList(list => list.filter(x => x.listing_id !== listing_id));
      toast.success("Rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reject");
    }
  };

  const parseCSVClientSide = (text) => {
    // Minimal RFC-4180-ish parser: handles quoted fields with commas + escaped double-quotes.
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const parseLine = (line) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (inQ) {
          if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (c === '"') inQ = false;
          else cur += c;
        } else {
          if (c === '"') inQ = true;
          else if (c === ",") { out.push(cur); cur = ""; }
          else cur += c;
        }
      }
      out.push(cur);
      return out;
    };
    const headers = parseLine(lines[0]).map(h => h.trim().toLowerCase());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = parseLine(lines[i]);
      const rec = {};
      headers.forEach((h, idx) => { rec[h] = (cells[idx] ?? "").trim(); });
      const title = rec.title || rec.name || rec.business_name;
      const lat = parseFloat(rec.lat || rec.latitude);
      const lng = parseFloat(rec.lng || rec.lon || rec.longitude);
      if (!title || isNaN(lat) || isNaN(lng)) continue;
      rows.push({
        title,
        category: (rec.category || "store").toLowerCase(),
        city: rec.city || "",
        address: rec.address || "",
        lat, lng,
        price: parseFloat(rec.price || 0) || 0,
        price_unit: rec.price_unit || "fixed",
        description: rec.description || "",
        phone: rec.phone || rec.mobile || null,
        website: rec.website || rec.url || null,
        tags: (rec.tags || "").split(",").map(t => t.trim()).filter(Boolean),
        images: (rec.images || rec.photo || "").split(",").map(t => t.trim()).filter(Boolean),
      });
    }
    return rows;
  };

  const onFilePicked = async (file) => {
    if (!file) return;
    const text = await file.text();
    try {
      let rows;
      if (file.name.toLowerCase().endsWith(".json")) {
        const parsed = JSON.parse(text);
        rows = Array.isArray(parsed) ? parsed : (parsed.rows || []);
      } else {
        rows = parseCSVClientSide(text);
      }
      if (!rows.length) {
        toast.error("No valid rows. Need columns: title, lat, lng (+ optional category, city, price, phone, description, tags, images).");
        return;
      }
      setImportPreview({ rows, filename: file.name });
      toast.success(`${rows.length} rows ready to import`);
    } catch (e) {
      toast.error(`Parse failed: ${e.message}`);
    }
  };

  const runImport = async () => {
    if (!importPreview?.rows?.length) return;
    setImporting(true);
    try {
      const r = await api.post("/ops/import-listings", {
        rows: importPreview.rows,
        source_label: importSourceLabel || "partner_import",
        partner_name: importPartnerName || "Ops CSV Import",
      });
      toast.success(`Imported: ${r.data.inserted} new, ${r.data.updated} updated`);
      setImportPreview(null);
      // Refresh sources so the count updates.
      api.get("/discover/sources").then(r => setSourceStatus(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const createPartner = async () => {
    if (!partnerForm.partner_slug || !partnerForm.partner_name) {
      toast.error("Slug and display name are required");
      return;
    }
    setCreatingPartner(true);
    try {
      const r = await api.post("/ops/partners", partnerForm);
      setNewPartnerToken(r.data);
      setPartnerForm({ partner_slug: "", partner_name: "", description: "" });
      api.get("/ops/partners").then(r => setPartners(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create partner");
    } finally {
      setCreatingPartner(false);
    }
  };

  const revokePartner = async (slug) => {
    if (!window.confirm(`Revoke partner "${slug}"? Their webhook will stop accepting data immediately.`)) return;
    try {
      await api.delete(`/ops/partners/${slug}`);
      toast.success("Partner revoked");
      api.get("/ops/partners").then(r => setPartners(r.data)).catch(() => {});
    } catch (e) {
      toast.error("Failed to revoke");
    }
  };

  const openAssign = (r) => {
    setSelected(r);
    setAssignForm({
      courier_partner: r.courier_partner || "",
      courier_tracking_id: r.courier_tracking_id || "",
      ops_notes: r.ops_notes || "",
    });
  };

  const submitAssign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/ops/redemptions/${selected.redemption_id}/assign`, assignForm);
      toast.success("Courier assigned");
      setSelected(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally {
      setSaving(false);
    }
  };

  const settleOne = async (r) => {
    if (!window.confirm(`Mark ₹${r.settlement_amount_inr} as settled to ${r.dealer_name}?`)) return;
    try {
      await api.patch(`/ops/redemptions/${r.redemption_id}/settle`);
      toast.success("Marked as settled");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not settle");
    }
  };

  const kpis = useMemo(() => stats ? [
    { key: "pending", label: "Pending pickup", value: stats.pending_pickup, icon: Clock, tone: "text-amber-700 bg-amber-50" },
    { key: "delivered", label: "Delivered", value: stats.delivered, icon: Truck, tone: "text-emerald-700 bg-emerald-50" },
    { key: "settled", label: "Settled", value: stats.settled, icon: CheckCircle2, tone: "text-[#2D5A27] bg-[#2D5A27]/10" },
    { key: "unsettled", label: "Unsettled ₹", value: `₹${Number(stats.unsettled_amount_inr).toLocaleString("en-IN")}`, icon: IndianRupee, tone: "text-[#E05E36] bg-[#E05E36]/10" },
    { key: "total", label: "All redemptions", value: stats.total_redemptions, icon: Package, tone: "text-stone-800 bg-stone-100" },
  ] : [], [stats]);

  // -- auth gates --
  if (authLoading) return <div className="max-w-7xl mx-auto px-5 py-24 text-stone-500">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="ops-login-gate">
        <ShieldCheck size={32} className="mx-auto text-[#2D5A27] mb-3" />
        <h1 className="heading text-3xl font-extrabold mb-2">Ops Dashboard</h1>
        <p className="text-stone-600 mb-6">Login with your admin Google account to continue.</p>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="ops-login-btn">Login with Google</Button>
      </div>
    );
  }
  if (!isAdmin || forbidden) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="ops-forbidden">
        <ShieldCheck size={32} className="mx-auto text-[#E05E36] mb-3" />
        <h1 className="heading text-3xl font-extrabold mb-2">Not authorised</h1>
        <p className="text-stone-600">This dashboard is for Find2Near operations admins only. Ask an existing admin to add <code className="text-xs bg-stone-100 rounded px-1.5 py-0.5">{user.email}</code> to <code className="text-xs bg-stone-100 rounded px-1.5 py-0.5">ADMIN_EMAILS</code>.</p>
        <Link to="/" className="mt-6 inline-block text-sm font-bold text-[#E05E36]">← Back home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Ops Console</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900" data-testid="ops-heading">{OPS_HEAD[tab]?.[0] || OPS_HEAD.redemptions[0]}</h1>
          <p className="text-stone-600 mt-1">{OPS_HEAD[tab]?.[1] || OPS_HEAD.redemptions[1]}</p>
        </div>
        <div className="text-xs text-stone-500 flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#2D5A27]" /> Signed in as <b className="text-stone-800">{user.email}</b></div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="ops-kpis">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <motion.div
              key={k.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-white border border-stone-200 rounded-2xl p-4"
              data-testid={`ops-kpi-${k.key}`}
            >
              <div className={`w-9 h-9 rounded-xl grid place-items-center ${k.tone}`}><Icon size={16} /></div>
              <div className="mt-2 heading text-2xl font-extrabold text-stone-900">{k.value ?? "—"}</div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">{k.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="ops-tabs">
        {[
          { key: "redemptions", label: "Redemptions", icon: Package },
          { key: "notifications", label: "SMS log", icon: MessageSquare },
          { key: "payouts", label: "Payouts", icon: Wallet },
          { key: "feedback", label: "User feedback", icon: Bug },
          { key: "sources", label: "Data sources", icon: Database },
          { key: "market", label: "Market Rates", icon: IndianRupee },
          { key: "ott", label: "OTT moderation", icon: Package },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full border transition-colors ${active ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"}`}
              data-testid={`ops-tab-${t.key}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "redemptions" && (
      <>
      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-2xl p-3 flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5 text-xs font-bold text-stone-600 px-2"><Filter size={13} /> Status</div>
        <div className="flex flex-wrap gap-1">
          {STATUS_TABS.map(s => (
            <button
              key={s.key || "all"}
              onClick={() => setStatusFilter(s.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${statusFilter === s.key ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"}`}
              data-testid={`ops-status-${s.key || "all"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={settleFilter || "any"} onValueChange={v => setSettleFilter(v === "any" ? "" : v)}>
            <SelectTrigger className="rounded-full h-9 text-xs w-44" data-testid="ops-settle-filter"><SelectValue placeholder="Any settlement" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any settlement</SelectItem>
              <SelectItem value="pending">Settlement pending</SelectItem>
              <SelectItem value="paid">Settlement paid</SelectItem>
              <SelectItem value="disputed">Settlement disputed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-bold text-stone-500">
          <div className="col-span-3">Customer / Dealer</div>
          <div className="col-span-2">Metal</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="py-16 text-center text-stone-500 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-stone-500" data-testid="ops-empty">
            <Package size={28} className="mx-auto text-stone-300 mb-2" />
            No assisted redemptions match these filters.
          </div>
        ) : rows.map((r) => (
          <div key={r.redemption_id} className="grid md:grid-cols-12 gap-3 px-5 py-4 border-b border-stone-100 items-center" data-testid={`ops-row-${r.redemption_id}`}>
            <div className="md:col-span-3">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400">#{r.redemption_id.slice(-6)}</div>
              <div className="flex items-center gap-1.5 mt-0.5 text-sm font-bold text-stone-900"><UserIcon size={13} /> {r.user_name}</div>
              {r.user_mobile && <div className="text-xs text-stone-500 flex items-center gap-1"><Phone size={11} /> {r.user_mobile}</div>}
              <div className="text-xs text-stone-500 mt-1">→ {r.dealer_name}</div>
              <div className="text-[11px] text-stone-500 flex items-center gap-1"><MapPin size={10} /> {r.dealer_city}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-sm font-bold text-stone-900 capitalize">{r.metal}</div>
              <div className="text-xs text-stone-600">{r.quantity_g} g</div>
              <div className="text-[11px] text-stone-500">₹{Number(r.price_per_g_at_request).toLocaleString("en-IN")}/g</div>
            </div>
            <div className="md:col-span-2">
              <div className="heading font-extrabold text-[#E05E36]">₹{Number(r.settlement_amount_inr).toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-stone-500">Market ₹{Number(r.market_value_inr).toLocaleString("en-IN")}</div>
              {r.transport_fee_inr > 0 && <div className="text-[11px] text-[#2D5A27]"><Truck size={10} className="inline" /> ₹{r.transport_fee_inr} logistics</div>}
            </div>
            <div className="md:col-span-2 space-y-1">
              <div className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_STYLES[r.status] || "bg-stone-100 border-stone-200 text-stone-700"}`}>
                {r.status}
              </div>
              <div className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${r.settlement_status === "paid" ? "bg-[#2D5A27]/10 border-[#2D5A27]/20 text-[#2D5A27]" : "bg-stone-50 border-stone-200 text-stone-600"}`}>
                ₹ {r.settlement_status}
              </div>
              {r.courier_partner && <div className="text-[11px] text-stone-500">🚚 {r.courier_partner}{r.courier_tracking_id ? ` · ${r.courier_tracking_id}` : ""}</div>}
            </div>
            <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end">
              <Button
                onClick={() => openAssign(r)}
                variant="outline"
                size="sm"
                className="rounded-full text-xs gap-1.5"
                data-testid={`ops-assign-${r.redemption_id}`}
              >
                <Truck size={13} /> {r.courier_partner ? "Update" : "Assign"} courier
              </Button>
              {r.settlement_status !== "paid" && (
                <Button
                  onClick={() => settleOne(r)}
                  size="sm"
                  className="rounded-full text-xs gap-1.5 bg-[#2D5A27] hover:bg-[#24491f]"
                  data-testid={`ops-settle-${r.redemption_id}`}
                >
                  <IndianRupee size={13} /> Mark settled
                </Button>
              )}
              <Link
                to={`/track/${r.redemption_id}`}
                className="inline-flex items-center gap-1 text-xs font-bold text-stone-600 hover:text-[#E05E36] px-2 self-center"
                data-testid={`ops-view-${r.redemption_id}`}
              >
                Details <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {tab === "notifications" && (
        <div className="space-y-4" data-testid="ops-notifications-tab">
          {/* Test SMS panel */}
          <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={16} className="text-[#E05E36]" />
              <div className="font-bold text-stone-900">Send test SMS</div>
              <div className="ml-auto text-[11px] text-stone-500">Verifies your provider is wired correctly.</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Input
                value={testForm.to}
                onChange={e => setTestForm(f => ({ ...f, to: e.target.value }))}
                placeholder="Phone (+91…)"
                className="rounded-full w-56"
                data-testid="ops-test-to"
              />
              <Input
                value={testForm.body}
                onChange={e => setTestForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Message"
                className="rounded-full flex-1 min-w-[240px]"
                data-testid="ops-test-body"
              />
              <Button
                onClick={async () => {
                  if (!testForm.to) return toast.error("Phone required");
                  setSendingTest(true);
                  try {
                    const r = await api.post("/ops/test-sms", null, { params: testForm });
                    if (r.data.status === "sent") toast.success("SMS sent");
                    else if (r.data.status === "would_send") toast.info("Logged — configure Twilio/MSG91 to actually send");
                    else toast.warning(`Status: ${r.data.status}`);
                    loadAll();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed");
                  } finally { setSendingTest(false); }
                }}
                disabled={sendingTest}
                className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-1.5"
                data-testid="ops-test-send"
              >
                {sendingTest ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
              </Button>
            </div>
            {stats && !stats.provider_configured && (
              <div className="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <div>
                  No SMS provider configured. Add <code className="bg-white/60 rounded px-1">TWILIO_ACCOUNT_SID</code>, <code className="bg-white/60 rounded px-1">TWILIO_AUTH_TOKEN</code>, <code className="bg-white/60 rounded px-1">TWILIO_FROM_NUMBER</code> (or MSG91 keys) to <code className="bg-white/60 rounded px-1">backend/.env</code> and restart backend. Until then, all outbound SMS are logged here with <b>would_send</b> status.
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-bold text-stone-500">
              Recent notifications ({notifs.length})
            </div>
            {notifs.length === 0 ? (
              <div className="py-10 text-center text-stone-500" data-testid="ops-notifs-empty">No notifications yet.</div>
            ) : notifs.map(n => (
              <div key={n.notification_id} className="px-5 py-3 border-b border-stone-100 flex items-start gap-3" data-testid={`ops-notif-${n.notification_id}`}>
                <div className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border shrink-0 ${
                  n.status === "sent" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                  n.status === "would_send" ? "bg-amber-50 text-amber-800 border-amber-200" :
                  n.status === "skipped" ? "bg-stone-100 text-stone-600 border-stone-200" :
                  "bg-red-50 text-red-800 border-red-200"
                }`}>{n.status}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-stone-500">{n.kind} → <b className="text-stone-800">{n.to || "—"}</b></div>
                  <div className="text-sm text-stone-800 truncate">{n.body}</div>
                  {n.error && <div className="text-[11px] text-red-700 mt-0.5">⚠ {n.error}</div>}
                </div>
                <div className="text-[10px] text-stone-400 shrink-0">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "payouts" && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="ops-payouts-tab">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-bold text-stone-500">
            Recent payouts ({payouts.length})
          </div>
          {payouts.length === 0 ? (
            <div className="py-10 text-center text-stone-500" data-testid="ops-payouts-empty">No payouts triggered yet.</div>
          ) : payouts.map(p => (
            <div key={p.payout_id} className="px-5 py-4 border-b border-stone-100 flex items-start gap-3" data-testid={`ops-payout-${p.payout_id}`}>
              <div className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border shrink-0 ${
                p.status === "initiated" || p.status === "processed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                p.status === "would_pay" ? "bg-amber-50 text-amber-800 border-amber-200" :
                "bg-red-50 text-red-800 border-red-200"
              }`}>{p.status}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-stone-500">Dealer: <b className="text-stone-800">{p.dealer_name || p.dealer_id}</b></div>
                <div className="heading font-extrabold text-[#E05E36]">₹{Number(p.amount_inr).toLocaleString("en-IN")}</div>
                {p.provider_payout_id && <div className="text-[11px] text-stone-500">Ref: {p.provider_payout_id}</div>}
                {p.error && <div className="text-[11px] text-red-700 mt-0.5">⚠ {p.error}</div>}
              </div>
              <div className="text-[10px] text-stone-400 shrink-0">{new Date(p.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "feedback" && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="ops-feedback-tab">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
              User feedback ({feedback.length})
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { key: "", label: "All" },
                { key: "bug", label: "🐛 Bugs" },
                { key: "idea", label: "💡 Ideas" },
                { key: "praise", label: "❤️ Praise" },
                { key: "other", label: "✳️ Other" },
              ].map(f => (
                <button
                  key={f.key || "all"}
                  onClick={() => setFeedbackKind(f.key)}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                    feedbackKind === f.key ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
                  }`}
                  data-testid={`ops-feedback-filter-${f.key || "all"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {feedback.length === 0 ? (
            <div className="py-10 text-center text-stone-500" data-testid="ops-feedback-empty">
              No feedback yet. Users can send it via the &ldquo;Report a bug&rdquo; button.
            </div>
          ) : feedback.map(fb => (
            <div key={fb.feedback_id} className={`px-5 py-4 border-b border-stone-100 flex items-start gap-3 ${
              fb.status === "resolved" ? "bg-stone-50/60 opacity-70" : ""
            }`} data-testid={`ops-feedback-${fb.feedback_id}`}>
              <div className="text-2xl leading-none mt-1 shrink-0">
                {fb.kind === "bug" ? "🐛" : fb.kind === "idea" ? "💡" : fb.kind === "praise" ? "❤️" : "✳️"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    fb.kind === "bug" ? "bg-red-50 text-red-800 border-red-200" :
                    fb.kind === "idea" ? "bg-amber-50 text-amber-800 border-amber-200" :
                    fb.kind === "praise" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                    "bg-stone-100 text-stone-700 border-stone-200"
                  }`}>{fb.kind}</span>
                  {fb.status === "resolved" && (
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1">
                      <Check size={10} /> Resolved
                    </span>
                  )}
                  <span className="text-xs text-stone-500">
                    {fb.user_name || fb.email || "Anonymous"} · {new Date(fb.created_at).toLocaleString()}
                  </span>
                  {fb.page && (
                    <span className="text-[10px] text-stone-400 font-mono truncate max-w-[220px]" title={fb.page}>
                      {fb.page}
                    </span>
                  )}
                </div>
                <div className={`mt-1.5 text-sm text-stone-800 whitespace-pre-line leading-snug ${fb.status === "resolved" ? "line-through decoration-stone-400" : ""}`}>
                  {fb.message}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {fb.has_screenshot && (
                    <button
                      onClick={async () => {
                        setLoadingShot(true);
                        try {
                          const r = await api.get(`/ops/feedback/${fb.feedback_id}`);
                          setLightbox({ url: r.data.screenshot, feedback_id: fb.feedback_id, meta: r.data });
                        } catch (e) {
                          toast.error("Could not load screenshot.");
                        } finally {
                          setLoadingShot(false);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#2D5A27] hover:text-[#E05E36]"
                      data-testid={`ops-feedback-view-shot-${fb.feedback_id}`}
                    >
                      <ImageIcon size={12} /> View screenshot
                    </button>
                  )}
                  {fb.status === "resolved" ? (
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/ops/feedback/${fb.feedback_id}`, { status: "new" });
                          setFeedback(list => list.map(x => x.feedback_id === fb.feedback_id ? { ...x, status: "new" } : x));
                          toast.success("Reopened.");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to reopen");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-stone-600 hover:text-stone-900"
                      data-testid={`ops-feedback-reopen-${fb.feedback_id}`}
                    >
                      <Undo2 size={12} /> Reopen
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/ops/feedback/${fb.feedback_id}`, { status: "resolved" });
                          setFeedback(list => list.map(x => x.feedback_id === fb.feedback_id ? { ...x, status: "resolved" } : x));
                          toast.success("Marked resolved.");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 hover:text-emerald-900"
                      data-testid={`ops-feedback-resolve-${fb.feedback_id}`}
                    >
                      <Check size={12} /> Mark resolved
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "market" && <GoldSettingsPanel />}
      {tab === "ott" && <OttModerationPanel />}

      {tab === "sources" && (
        <div className="space-y-5" data-testid="ops-sources-tab">
          {/* Pending public self-listings — highest priority (users waiting for approval) */}
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <AlertCircle size={14} className="text-amber-700" />
              <div className="text-[11px] uppercase tracking-widest font-bold text-amber-900">
                Pending self-listings — awaiting verification ({pendingList.length})
              </div>
            </div>
            {pendingList.length === 0 ? (
              <div className="px-5 py-4 text-xs text-stone-500">
                No pending submissions. When people fill the <code className="bg-stone-100 rounded px-1">/list-my-business</code> form, they&apos;ll appear here for a quick verify-by-phone call.
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {pendingList.map(p => (
                  <div key={p.listing_id} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`ops-pending-${p.listing_id}`}>
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-bold text-sm text-stone-900">{p.title}</div>
                      <div className="text-xs text-stone-500 truncate">
                        {p.category} · {p.city || `${p.lat?.toFixed(3)}, ${p.lng?.toFixed(3)}`} · {p.owner_mobile}
                      </div>
                      {p.description && <div className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{p.description}</div>}
                    </div>
                    <a
                      href={`tel:${p.owner_mobile}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2D5A27] hover:text-[#E05E36]"
                      data-testid={`ops-pending-call-${p.listing_id}`}
                    >
                      <Phone size={12} /> Call
                    </a>
                    <button
                      onClick={() => approvePending(p.listing_id)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900"
                      data-testid={`ops-pending-approve-${p.listing_id}`}
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => rejectPending(p.listing_id)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-800"
                      data-testid={`ops-pending-reject-${p.listing_id}`}
                    >
                      <Trash2 size={12} /> Reject
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active sources */}
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-2">
              <Database size={14} className="text-[#2D5A27]" />
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                POI &amp; contact-data sources
              </div>
            </div>
            {!sourceStatus ? (
              <div className="p-10 text-center text-stone-500"><Loader2 size={16} className="inline animate-spin mr-2" />Loading sources…</div>
            ) : (
              <div className="divide-y divide-stone-100">
                {sourceStatus.active_sources.map(s => (
                  <div key={s.slug} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`ops-source-${s.slug}`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${s.live ? "bg-emerald-500" : "bg-stone-300"}`} />
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-bold text-sm text-stone-900">{s.name}</div>
                      <div className="text-xs text-stone-500">{s.notes}</div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.live ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
                      {s.live ? "Active" : "Inactive"}
                    </span>
                    {s.env_key && !s.live && (
                      <code className="text-[10px] font-mono bg-amber-50 text-amber-900 border border-amber-200 px-1.5 py-0.5 rounded">
                        Set {s.env_key}
                      </code>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CSV / JSON import */}
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-2">
              <Upload size={14} className="text-[#E05E36]" />
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                Bulk import (CSV or JSON you own legally)
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Source label</label>
                  <Input
                    value={importSourceLabel}
                    onChange={e => setImportSourceLabel(e.target.value)}
                    placeholder="partner_import"
                    className="mt-1.5 rounded-xl"
                    data-testid="ops-import-source-label"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Partner / owner name</label>
                  <Input
                    value={importPartnerName}
                    onChange={e => setImportPartnerName(e.target.value)}
                    placeholder="e.g. Delhi Chamber of Commerce"
                    className="mt-1.5 rounded-xl"
                    data-testid="ops-import-partner-name"
                  />
                </div>
              </div>
              <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center bg-stone-50/50">
                <input
                  id="ops-import-file"
                  type="file"
                  accept=".csv,.json,text/csv,application/json"
                  className="hidden"
                  onChange={e => onFilePicked(e.target.files?.[0])}
                  data-testid="ops-import-file-input"
                />
                <label htmlFor="ops-import-file" className="cursor-pointer inline-flex items-center gap-2 text-sm font-bold text-[#2D5A27] hover:text-[#E05E36]">
                  <Upload size={16} /> Choose CSV / JSON file
                </label>
                <div className="text-xs text-stone-500 mt-2">
                  Required columns: <code className="bg-white border border-stone-200 rounded px-1">title</code>, <code className="bg-white border border-stone-200 rounded px-1">lat</code>, <code className="bg-white border border-stone-200 rounded px-1">lng</code>.
                  Optional: <code>category, city, address, price, phone, website, description, tags, images</code>.
                </div>
              </div>

              {importPreview && (
                <div className="border border-stone-200 rounded-xl p-4 bg-emerald-50/40" data-testid="ops-import-preview">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-sm text-stone-800">{importPreview.filename} — {importPreview.rows.length} rows</div>
                    <button onClick={() => setImportPreview(null)} className="text-xs text-stone-500 hover:text-red-600" data-testid="ops-import-clear">Clear</button>
                  </div>
                  <div className="max-h-40 overflow-auto text-xs text-stone-700 space-y-0.5 font-mono">
                    {importPreview.rows.slice(0, 8).map((r, i) => (
                      <div key={i} className="truncate">
                        {i + 1}. {r.title} — {r.category} — {r.city || `${r.lat},${r.lng}`}
                      </div>
                    ))}
                    {importPreview.rows.length > 8 && (
                      <div className="text-stone-400">… and {importPreview.rows.length - 8} more</div>
                    )}
                  </div>
                  <Button
                    onClick={runImport}
                    disabled={importing}
                    className="mt-3 rounded-full bg-[#E05E36] hover:bg-[#c94e2a]"
                    data-testid="ops-import-run"
                  >
                    {importing ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Importing…</> : <>Import {importPreview.rows.length} rows</>}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Partner tokens */}
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-2">
              <KeyRound size={14} className="text-[#2D5A27]" />
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                Partner webhook tokens ({partners.length})
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid md:grid-cols-3 gap-2">
                <Input
                  value={partnerForm.partner_slug}
                  onChange={e => setPartnerForm(f => ({ ...f, partner_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))}
                  placeholder="partner-slug (kebab-case)"
                  className="rounded-xl"
                  data-testid="ops-partner-slug"
                />
                <Input
                  value={partnerForm.partner_name}
                  onChange={e => setPartnerForm(f => ({ ...f, partner_name: e.target.value }))}
                  placeholder="Partner display name"
                  className="rounded-xl"
                  data-testid="ops-partner-name"
                />
                <Button onClick={createPartner} disabled={creatingPartner} className="rounded-full bg-stone-900 hover:bg-stone-800" data-testid="ops-partner-create">
                  {creatingPartner ? <Loader2 size={14} className="animate-spin" /> : "Generate token"}
                </Button>
              </div>

              {newPartnerToken && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-xs" data-testid="ops-partner-new-token">
                  <div className="flex items-center gap-2 font-bold text-amber-900 mb-1">
                    <AlertCircle size={12} /> Save this token — it will not be shown again
                  </div>
                  <div className="font-mono text-[11px] break-all bg-white border border-amber-200 rounded p-2 flex items-start gap-2">
                    <div className="flex-1">{newPartnerToken.token}</div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(newPartnerToken.token); toast.success("Token copied"); }}
                      className="shrink-0 text-[#2D5A27] hover:text-[#E05E36]"
                      title="Copy"
                      data-testid="ops-partner-copy"
                    ><Copy size={12} /></button>
                  </div>
                  <div className="mt-2 text-stone-600">
                    Webhook: <code className="bg-white border border-stone-200 rounded px-1">POST {newPartnerToken.webhook_url}</code> with header <code className="bg-white border border-stone-200 rounded px-1">X-Partner-Token</code>
                  </div>
                  <button onClick={() => setNewPartnerToken(null)} className="mt-2 text-stone-500 hover:text-stone-900 text-[11px] font-bold">Dismiss</button>
                </div>
              )}

              {partners.length === 0 ? (
                <div className="text-xs text-stone-500 text-center py-4">No partners yet. Create one above to give an external partner a push-data webhook.</div>
              ) : (
                <div className="divide-y divide-stone-100 border border-stone-200 rounded-xl">
                  {partners.map(p => (
                    <div key={p.partner_slug} className="px-4 py-2.5 flex items-center gap-3" data-testid={`ops-partner-${p.partner_slug}`}>
                      <div className={`w-2 h-2 rounded-full ${p.active ? "bg-emerald-500" : "bg-stone-300"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-stone-900 truncate">{p.partner_name}</div>
                        <div className="text-[11px] text-stone-500 font-mono truncate">{p.partner_slug} · {p.ingest_count || 0} rows ingested</div>
                      </div>
                      {p.active && (
                        <button
                          onClick={() => revokePartner(p.partner_slug)}
                          className="text-xs text-red-600 hover:text-red-800 font-bold inline-flex items-center gap-1"
                          data-testid={`ops-partner-revoke-${p.partner_slug}`}
                        >
                          <Trash2 size={12} /> Revoke
                        </button>
                      )}
                      {!p.active && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Revoked</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Closed platforms */}
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center gap-2">
              <Lock size={14} className="text-red-600" />
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">
                Closed platforms — why we can&apos;t scrape
              </div>
            </div>
            <div className="p-5">
              {sourceStatus?.note && (
                <div className="text-xs text-stone-600 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  {sourceStatus.note}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-2">
                {sourceStatus?.closed_platforms?.map(p => (
                  <div key={p.slug} className="border border-stone-200 rounded-xl p-3" data-testid={`ops-closed-${p.slug}`}>
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-sm text-stone-900">{p.name}</div>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{p.category}</span>
                    </div>
                    <div className="text-xs text-stone-600 mt-1">{p.reason}</div>
                    {p.alternative && (
                      <div className="text-xs text-emerald-700 mt-1"><b>Alternative:</b> {p.alternative}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="ops-feedback-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 grid place-items-center text-stone-800 hover:bg-white shadow-lg"
            aria-label="Close screenshot"
            data-testid="ops-feedback-lightbox-close"
          >
            <CloseX size={16} />
          </button>
          <div className="max-w-6xl max-h-[90vh] flex flex-col gap-3" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.url}
              alt="Feedback screenshot"
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border border-white/20"
              data-testid="ops-feedback-screenshot-img"
            />
            <div className="text-[11px] text-white/80 font-mono truncate">
              {lightbox.meta?.page} · {lightbox.meta?.user_agent?.slice(0, 80)}
            </div>
          </div>
        </div>
      )}
      {loadingShot && (
        <FloatingSlot zone="bottom-left" order={2} size="badge" zIndex="badge" hideOnKeyboard={false}>
          <div className="bg-white border border-stone-200 shadow-lg rounded-full px-4 py-2 text-xs font-bold flex items-center gap-2" data-testid="ops-loading-pill">
            <Loader2 size={14} className="animate-spin" /> Loading screenshot…
          </div>
        </FloatingSlot>
      )}

      {/* Assign courier dialog */}
      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="ops-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign courier</DialogTitle>
            <DialogDescription>
              Log the logistics partner handling this pickup so the customer and dealer stay in sync.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Courier partner</label>
              <Select value={assignForm.courier_partner || "custom"} onValueChange={v => setAssignForm(f => ({ ...f, courier_partner: v === "custom" ? "" : v }))}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="ops-partner-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Porter">Porter</SelectItem>
                  <SelectItem value="Dunzo">Dunzo</SelectItem>
                  <SelectItem value="Blue Dart">Blue Dart</SelectItem>
                  <SelectItem value="Delhivery">Delhivery</SelectItem>
                  <SelectItem value="In-house">In-house team</SelectItem>
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {!["Porter", "Dunzo", "Blue Dart", "Delhivery", "In-house"].includes(assignForm.courier_partner) && (
                <Input
                  value={assignForm.courier_partner}
                  onChange={e => setAssignForm(f => ({ ...f, courier_partner: e.target.value }))}
                  placeholder="Enter partner name"
                  className="mt-2 rounded-xl"
                  data-testid="ops-partner-custom"
                />
              )}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Tracking ID (optional)</label>
              <Input
                value={assignForm.courier_tracking_id}
                onChange={e => setAssignForm(f => ({ ...f, courier_tracking_id: e.target.value }))}
                placeholder="e.g. PTR-4832"
                className="mt-1.5 rounded-xl"
                data-testid="ops-tracking-id"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Ops notes (internal)</label>
              <Textarea
                value={assignForm.ops_notes}
                onChange={e => setAssignForm(f => ({ ...f, ops_notes: e.target.value }))}
                placeholder="Handling instructions, contact person…"
                className="mt-1.5 rounded-xl"
                data-testid="ops-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full" data-testid="ops-assign-cancel">Cancel</Button>
            <Button onClick={submitAssign} disabled={saving} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="ops-assign-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
